import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE, FARM_FOOTPRINTING_DRYING_ENERGY_TYPE, FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES } from "models";

import { Card, Flex, Form, FormInstance, Icon, InputNumber, KlimRadioGroup, Option, PageSpin, Select, Tooltip } from "components";

type DryingConsumptionProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

export const CropDryingForm = ({ primaryDataId, readOnly, farmId, cropId, year }: DryingConsumptionProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [dryingActive, setDryingActive] = useState<boolean | null>(null);
  const [energyType, setEnergyType] = useState<ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE | null>(null);
  const [fuelAmount, setFuelAmount] = useState<number | null>(null);
  const [renewableEnergyPct, setRenewableEnergyPct] = useState<number | null>(null);
  const [postDryingMoistureContentPct, setPostDryingMoistureContentPct] = useState<number | null>(null);
  const [driedWeightTonsPerHa, setDriedWeightTonsPerHa] = useState<number | null>(null);
  const fuelAmountSuffix = energyType ? FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES[energyType] || "" : "";

  const selectGroupName = "footprinting-crop-energy-select_energy-type";

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      energyType,
      fuelAmount,
      renewableEnergyPct,
      postDryingMoistureContentPct,
      driedWeightTonsPerHa,
      dryingActive,
    },
    primaryDataId
  );

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setDryingActive(cropPrimaryDataQuery.data.dryingActive ?? null);
      setEnergyType(cropPrimaryDataQuery.data.energyType ?? null);
      setFuelAmount(cropPrimaryDataQuery.data.fuelAmount ?? null);
      setRenewableEnergyPct(cropPrimaryDataQuery.data.renewableEnergyPct ?? null);
      setPostDryingMoistureContentPct(cropPrimaryDataQuery.data.postDryingMoistureContentPct ?? null);
      setDriedWeightTonsPerHa(cropPrimaryDataQuery.data.driedWeightTonsPerHa ?? null);

      formRef.current?.setFieldsValue({
        dryingActive: cropPrimaryDataQuery.data.dryingActive ?? null,
        energyType: cropPrimaryDataQuery.data.energyType ?? null,
        fuelAmount: cropPrimaryDataQuery.data.fuelAmount ?? null,
        renewableEnergyPct: formatFractionInput(cropPrimaryDataQuery.data.renewableEnergyPct ?? null),
        postDryingMoistureContentPct: formatFractionInput(cropPrimaryDataQuery.data.postDryingMoistureContentPct ?? null),
        driedWeightTonsPerHa: cropPrimaryDataQuery.data.driedWeightTonsPerHa ?? null,
      });

      formRef.current?.validateFields();
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  const isLoading = queryIsLoading(cropPrimaryDataQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropDryingForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropDryingForm.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-crop-drying-form" className="padding-100">
        <Form ref={formRef}>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <span>
              {t("cropDryingForm.question.dryingQuestionLabel")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropDryingForm.tooltips.dryingQuestionLabel")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              required
              dataTestId="footprinting-crop-drying-form-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setDryingActive(target.value);
              }}
              value={dryingActive}
              defaultValue={dryingActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {dryingActive && (
            <div>
              <Form.Item className="padding-100">
                {/* @ts-expect-error need to convert to tsx */}
                <Select
                  dataTestId={selectGroupName}
                  disabled={readOnly}
                  name="energyType"
                  label={t("cropDryingForm.question.dryingTypeQuestionLabel")}
                  tooltip={t("cropDryingForm.tooltips.dryingTypeQuestionLabel")}
                  rules={[{ required: true, message: false }]}
                  initialValue={cropPrimaryDataQuery.data?.energyType}
                  value={energyType}
                  onChange={(v) => setEnergyType(v)}
                  dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                >
                  {Object.values(FARM_FOOTPRINTING_DRYING_ENERGY_TYPE).map((type) => (
                    <Option
                      disabled={readOnly}
                      key={`${selectGroupName}-${type}`}
                      data-testid={`${selectGroupName}-${type}`}
                      label={t(`cropDryingForm.dryingTypeOptions.${type}`)}
                      value={type}
                    >
                      {t(`cropDryingForm.dryingTypeOptions.${type}`)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {energyType && (
                <div>
                  <Form.Item className="padding-left-100 padding-right-100">
                    <InputNumber
                      className="floating-label"
                      disabled={readOnly}
                      name="fuelAmount"
                      dataTestId="footprinting-crop-drying-form-fuel-input"
                      label={t("cropDryingForm.question.fuelConsumptionQuestionLabel")}
                      tooltip={t("cropDryingForm.tooltips.fuelConsumptionQuestionLabel")}
                      suffix={fuelAmountSuffix}
                      value={fuelAmount}
                      initialValue={fuelAmount}
                      onChange={(v) => setFuelAmount(v)}
                      rules={[
                        { required: true, message: t("cropDryingForm.error.fuelConsumptionLabelError") },
                        { type: "number", message: t("cropDryingForm.error.fuelConsumptionLabelError") },
                        {
                          validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                          message: t("cropDryingForm.error.fuelConsumptionLabelError"),
                        },
                      ]}
                    />
                  </Form.Item>
                  {energyType === "electricity" && (
                    <Form.Item className="padding-left-100 padding-right-100 padding-top-100">
                      <InputNumber
                        className="floating-label"
                        disabled={readOnly}
                        name="renewableEnergyPct"
                        dataTestId="footprinting-crop-drying-form-renewable-energy-input"
                        label={t("cropDryingForm.question.renewableEnergyQuestionLabel")}
                        tooltip={t("cropDryingForm.tooltips.renewableEnergyQuestionLabel")}
                        suffix="%"
                        value={formatFractionInput(renewableEnergyPct)}
                        initialValue={formatFractionInput(renewableEnergyPct)}
                        onChange={(v) => setRenewableEnergyPct(formatPercentageInput(v))}
                        precision={2}
                        rules={[
                          { required: true, message: t("cropDryingForm.error.percentageEnergyLabelError") },
                          { type: "number", message: t("cropDryingForm.error.percentageEnergyLabelError") },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidPercentageDecimal(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropDryingForm.error.percentageEnergyLabelError"),
                          },
                        ]}
                      />
                    </Form.Item>
                  )}
                  <Form.Item className="padding-100 padding-bottom-0">
                    <InputNumber
                      className="floating-label"
                      disabled={readOnly}
                      name="postDryingMoistureContentPct"
                      dataTestId="footprinting-crop-drying-form-moisture-content-input"
                      label={t("cropDryingForm.question.moistureContentQuestionLabel")}
                      tooltip={t("cropDryingForm.tooltips.moistureContentQuestionLabel")}
                      suffix="%"
                      value={formatFractionInput(postDryingMoistureContentPct)}
                      initialValue={formatFractionInput(postDryingMoistureContentPct)}
                      onChange={(v) => setPostDryingMoistureContentPct(formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        { required: true, message: t("cropDryingForm.error.percentageEnergyLabelError") },
                        { type: "number", message: t("cropDryingForm.error.percentageEnergyLabelError") },
                        {
                          validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidPercentageDecimal(v) ? Promise.resolve() : Promise.reject()),
                          message: t("cropDryingForm.error.percentageEnergyLabelError"),
                        },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item className="padding-100">
                    <InputNumber
                      className="floating-label"
                      disabled={readOnly}
                      name="driedWeightTonsPerHa"
                      dataTestId="footprinting-crop-drying-form-dried-weight-input"
                      label={t("cropDryingForm.question.totalWeightQuestionLabel")}
                      tooltip={t("cropDryingForm.tooltips.totalWeightQuestionLabel")}
                      suffix="t/ha"
                      value={driedWeightTonsPerHa}
                      initialValue={driedWeightTonsPerHa}
                      onChange={(v) => setDriedWeightTonsPerHa(v)}
                      rules={[
                        { required: true, message: t("cropDryingForm.error.totalWeightLabelError") },
                        { type: "number", message: t("cropDryingForm.error.totalWeightLabelError") },
                        {
                          validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                          message: t("cropDryingForm.error.totalWeightLabelError"),
                        },
                      ]}
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          )}
        </Form>
      </Card>
    </div>
  );
};
